import type { TemplateFieldValueInOut } from '@festina/configuration-general-types/configuration'
import { errorMessageToastContent } from '@festina-life/components'
import { assertNotEmpty, type Optional } from '@festina-life/core'
import { IconButton } from '@festina-life/ui/actions'
import { toast } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { useMemo } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import {
  areTemplateClipboardTypesAssignable,
  type ClipboardTemplateFieldType,
  isClipboardData,
  isFunctionClipboardData,
  mappedClipboardValues,
} from '../../types/clipboard'
import {
  ConfigurationFieldTypeToInOutMapper,
  isConfigurationFunction,
  isTemplateField,
} from '../../types/templateModel'
import { isAddDynamicFunctionPlaceholder } from '../dynamicFunction/types'
import type { ChildAction } from '../stackableTable/types'
import { functionBody } from './functionHelpers'
import type { TemplateFieldChild } from './TemplateFieldsTable'

export const usePasteChildAction = (): Optional<
  ChildAction<TemplateFieldChild>
> => {
  const { isDraftTemplate } = useTemplateContext()
  const translate = useTranslate()

  return useMemo(
    () =>
      isDraftTemplate
        ? {
            actionId: 'pasteValue',
            title: translate('templateViewer.actions.paste'),
            test: (data) =>
              !data.isEmpty && !data.isInherited
                ? translate('clipboard.replaceValues')
                : true,
            action: (data) => {
              navigator.clipboard
                .readText()
                .then(async (clipboardText) => {
                  const parsedContent = clipboardText
                    ? JSON.parse(clipboardText)
                    : null

                  const supportedFieldTypes = [
                    'ConfigurationExpressionValueInOut',
                    'SimpleValueSetupInOut',
                    'TemplateValueSetupInOut',
                    'ConfigurationFunctionValueInOut',
                  ]

                  const isPasteSupported =
                    parsedContent &&
                    isClipboardData(parsedContent) &&
                    parsedContent.data._type &&
                    supportedFieldTypes.includes(parsedContent.data._type)

                  if (!isPasteSupported) {
                    throw new Error(
                      translate('clipboard.error.invalidClipboardData'),
                    )
                  }

                  if (
                    isTemplateField(data.value) &&
                    parsedContent.data._type &&
                    areTemplateClipboardTypesAssignable(
                      parsedContent.data._type as ClipboardTemplateFieldType,
                      ConfigurationFieldTypeToInOutMapper[
                        data.value.fieldType
                      ] as ClipboardTemplateFieldType,
                    )
                  ) {
                    const mappedValue = mappedClipboardValues(
                      parsedContent.data,
                      ConfigurationFieldTypeToInOutMapper[
                        data.value.fieldType
                      ] as ClipboardTemplateFieldType,
                    ) as TemplateFieldValueInOut
                    await data.value.updateRaw(mappedValue)
                  } else if (
                    !isAddDynamicFunctionPlaceholder(data.value) &&
                    isConfigurationFunction(data.value) &&
                    isFunctionClipboardData(parsedContent.data)
                  ) {
                    const value = parsedContent.data

                    if (value.function && data.value.type === 'declared') {
                      assertNotEmpty(data.value.descriptor?.signature)

                      const body = functionBody(value.function)

                      value.function = `${data.value.descriptor.signature} = \n    ${body}`
                    }

                    data.value.updateRaw(value)
                  } else if (
                    isAddDynamicFunctionPlaceholder(data.value) &&
                    isFunctionClipboardData(parsedContent.data)
                  ) {
                    data.value.addRawFunction(parsedContent.data)
                  } else {
                    // The translation needs to be extracted to its own variable in order for the unused translations script to not report a false positive.
                    const unknownTypeTranslation = translate(
                      'clipboard.itemType.unknown',
                    )
                    toast({
                      title: translate('clipboard.error.invalidTypes', {
                        clipboardType:
                          parsedContent.data._type ?? unknownTypeTranslation,
                        destinationType: isTemplateField(data.value)
                          ? data.value.fieldType
                          : 'ConfigurationFunctionValue',
                      }),
                      variant: 'success',
                    })
                  }
                })
                .catch((error: Error) => {
                  toast(errorMessageToastContent({ error, translate }))
                })
            },
            node: () => <IconButton iconName="ClipboardDocumentList" />,
          }
        : null,
    [isDraftTemplate, translate],
  )
}
